/* Bill Jewellers — motion engine
   Vanilla. No dependencies. All timing/easing lives in CSS custom properties
   (see css/motion.css) so the art direction can be retuned without touching JS.

   Responsibilities:
     1. reveal      — enter-viewport choreography with stagger
     2. parallax    — cheap transform-only depth on scroll
     3. splitText   — line/word/char wrapping for type reveals
     4. counters    — number count-ups
     5. progress    — scroll progress + header state
     6. marquee     — seamless infinite ticker
     7. pointerDepth— card tilt + cursor-tracking specular (now real)
   Everything is a no-op under prefers-reduced-motion except opacity settling.
*/
(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const raf = window.requestAnimationFrame.bind(window);

  /* ------------------------------------------------------------------ *
   * 3. splitText — must run BEFORE reveal so generated lines get observed
   * ------------------------------------------------------------------ */
  function splitText(root = document) {
    root.querySelectorAll('[data-split]').forEach((el) => {
      if (el.dataset.splitDone) return;
      const mode = el.dataset.split || 'word';
      const source = el.textContent.replace(/\s+/g, ' ').trim();

      if (mode === 'char') {
        el.textContent = '';
        [...source].forEach((ch, i) => {
          const span = document.createElement('span');
          span.className = 'split-char';
          span.style.setProperty('--i', i);
          span.textContent = ch === ' ' ? ' ' : ch;
          el.appendChild(span);
        });
      } else {
        // word mode: each word gets a clipping mask so it rises out of nothing
        el.textContent = '';
        source.split(' ').forEach((word, i) => {
          const mask = document.createElement('span');
          mask.className = 'split-mask';
          const inner = document.createElement('span');
          inner.className = 'split-word';
          inner.style.setProperty('--i', i);
          inner.textContent = word;
          mask.appendChild(inner);
          el.appendChild(mask);
          el.appendChild(document.createTextNode(' '));
        });
      }
      el.dataset.splitDone = '1';
    });
  }

  /* ------------------------------------------------------------------ *
   * 1. reveal
   * ------------------------------------------------------------------ */
  /* Chrome will not paint a loading="lazy" image inside a CSS multi-column
     container (measured on our-work.html — bitmap decoded, compositor never
     painted). EVERY path that reveals an element must force its lazy images,
     or the reduced-motion and safety-net paths ship the same grey tiles the
     observer path was fixed for. */
  function forcePaint(el) {
    if (!el.querySelectorAll) return;
    el.querySelectorAll('img[loading="lazy"]').forEach((im) => {
      im.loading = 'eager';
      if (im.decode) im.decode().catch(() => {});
    });
  }

  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (REDUCED.matches) {
      targets.forEach((el) => { el.classList.add('is-revealed'); forcePaint(el); });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          // stagger children of a group
          const delay = parseFloat(el.dataset.revealDelay || 0);
          if (delay) el.style.setProperty('--reveal-delay', `${delay}ms`);
          el.classList.add('is-revealed');

          forcePaint(el);

          io.unobserve(el);
        });
      },
      // fire a little before the element is fully on screen — motion should
      // already be finishing as the eye arrives, never starting.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    targets.forEach((el) => io.observe(el));

    /* ⛔ SAFETY NET — choreography must never be the only thing standing
       between a visitor and the content. Everything with [data-reveal] starts
       at opacity 0, and if this observer misses an element it stays invisible
       FOREVER with no fallback. Three separate casualties were measured:
         · our-work.html — 5 of 12 portfolio images, ~1200px of black on the
           page whose entire job is showing the work
         · about.html — the 985px `owner-at-logo-sign` portrait, the only
           photograph of a person on the whole site (its data-reveal="mask"
           path had effectively never worked)
         · appointments.html on mobile — the Where / Email / Socials block, so
           the shop's address and email were simply absent
       An unrevealed element is indistinguishable from a broken page, so after
       1.2s anything still hidden is shown regardless. The same fix is applied
       to the homepage's own reveal system in js/bj-elevate.js — this is the
       class of bug, not an instance of it. */
    window.addEventListener('load', () => {
      setTimeout(() => {
        targets.forEach((el) => {
          if (el.classList.contains('is-revealed')) return;
          if (parseFloat(getComputedStyle(el).opacity) < 0.01) {
            el.classList.add('is-revealed');
            forcePaint(el);
            io.unobserve(el);
          }
        });
      }, 1200);
    });

    // groups: auto-assign --i to children for CSS-side stagger
    document.querySelectorAll('[data-reveal-group]').forEach((group) => {
      [...group.children].forEach((child, i) => child.style.setProperty('--i', i));
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. parallax  (transform only — never top/background-position)
   * ------------------------------------------------------------------ */
  function initParallax() {
    if (REDUCED.matches) return;
    const layers = [...document.querySelectorAll('[data-parallax]')];
    if (!layers.length) return;

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      layers.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -vh || r.top > vh * 2) return; // offscreen, skip
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        // progress: -1 (below fold) → 1 (above fold)
        const progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.setProperty('--py', `${(progress * speed * 100).toFixed(2)}px`);
      });
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * 3b. scrub — the scroll-progress driver
   *
   * Writes 0→1 progress into --p on any [data-scrub] element as it travels
   * through the viewport. Everything scroll-linked hangs off this: pinned
   * sequences, hero scale, scrim depth. One rAF-throttled handler for the
   * whole page rather than a listener per effect.
   * ------------------------------------------------------------------ */
  function initScrub() {
    const els = [...document.querySelectorAll('[data-scrub]')];
    if (!els.length) return;

    if (REDUCED.matches) {
      // resting state: first frame, fully settled, no scroll coupling
      els.forEach((el) => {
        el.style.setProperty('--p', '0');
        el.querySelectorAll('[data-layer]').forEach((l, i) => {
          l.style.opacity = i === 0 ? '1' : '0';
        });
      });
      return;
    }

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        const span = r.height - vh;               // scrollable distance inside
        let p;
        if (span > 0) {
          p = (-r.top) / span;                    // pinned: 0 at entry, 1 at exit
        } else {
          // Viewport-height block (the hero). Progress is how far it has been
          // scrolled PAST, so it reads 0 while it fills the screen and climbs
          // as it leaves. The old enter-based formula started a full-height
          // hero at 0.5, i.e. already half-faded before anyone scrolled.
          p = (-r.top) / (r.height || 1);
        }
        p = Math.min(1, Math.max(0, p));
        el.style.setProperty('--p', p.toFixed(4));

        // cross-dissolving layers: opacity peaks as the playhead passes each
        const layers = el.querySelectorAll('[data-layer]');
        if (layers.length > 1) {
          const head = p * (layers.length - 1);
          layers.forEach((l, i) => {
            const d = Math.abs(head - i);
            l.style.opacity = String(Math.min(1, Math.max(0, 1 - d)));
          });
          // captions track the nearest frame
          const active = Math.round(head);
          el.querySelectorAll('[data-caption]').forEach((c, i) => {
            c.classList.toggle('is-active', i === active);
          });
        }
      });
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * 3c. hero video — autoplay is a request, not a guarantee
   * ------------------------------------------------------------------ */
  function initHeroVideo() {
    const v = document.querySelector('[data-hero-video]');
    if (!v) return;

    // NOTE: reduced-motion is checked at the point of PLAY, never here. An
    // early return used to skip installing the recovery handlers below, so a
    // video the browser had started anyway (via the old autoplay attribute)
    // could stop and never come back. Handlers always get installed; whether
    // we are allowed to start playback is a separate question.
    const mayPlay = () => !REDUCED.matches;

    let onScreen = false;
    let strikes = 0;                 // failed/stalled starts before we give up
    const MAX_STRIKES = 6;

    // Falling back is a real state, not a silent failure: the still underneath
    // becomes the hero and it reads as a deliberate choice rather than a
    // video that died mid-frame.
    const giveUp = () => {
      document.documentElement.classList.add('no-autoplay');
      v.removeEventListener('pause', onPause);
      clearInterval(watchdog);
    };

    const attempt = () => {
      if (document.hidden || !mayPlay() || !onScreen) return;
      if (!v.paused) return;
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => { if (++strikes >= MAX_STRIKES) giveUp(); });
      }
    };

    // Save the customer's data and the main thread when the hero is off
    // screen — a looping video behind three other sections is pure waste.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        onScreen = e.isIntersecting;
        if (onScreen) attempt();
        else if (!v.paused) v.pause();
      });
    }, { threshold: 0.05 });
    io.observe(v);

    // Chrome pauses muted video when the tab loses focus and does NOT resume
    // on return — a browser-initiated pause event fires with no JS in the
    // call stack. Without this the hero is frozen for anyone who tabbed away
    // once, which is nearly everyone.
    function onPause() { setTimeout(attempt, 120); }
    v.addEventListener('pause', onPause);
    document.addEventListener('visibilitychange', attempt);
    window.addEventListener('focus', attempt);
    window.addEventListener('pageshow', attempt);

    // The stall watchdog. A video can report paused===false and still not
    // advance a single frame — that is what a throttled or occluded element
    // does, and it is invisible to the pause event. Only currentTime moving
    // proves the hero is actually alive, so that is what we measure.
    let lastTime = -1;
    const watchdog = setInterval(() => {
      if (document.hidden || !onScreen || !mayPlay()) return;
      const moving = v.currentTime !== lastTime;
      lastTime = v.currentTime;
      if (moving) { strikes = 0; return; }
      if (++strikes >= MAX_STRIKES) giveUp();
      else attempt();
    }, 1000);

    attempt();

    // ── ?video-debug=1 ────────────────────────────────────────────────────
    // Nobody can debug this remotely: a stalled hero looks identical to a
    // working one in a screenshot, and every automated browser reports its
    // tab as hidden, which is itself enough to freeze playback. So the page
    // reports on itself. Opt-in only — never shipped to a real visitor.
    if (!/[?&]video-debug=1/.test(location.search)) return;
    const box = document.createElement('div');
    box.style.cssText = 'position:fixed;left:12px;bottom:12px;z-index:99999;' +
      'font:13px/1.5 ui-monospace,Menlo,monospace;background:rgba(0,0,0,.86);' +
      'color:#fff;padding:10px 14px;border:1px solid rgba(255,255,255,.25);' +
      'border-radius:8px;max-width:min(92vw,460px);white-space:pre-wrap';
    document.body.appendChild(box);
    let prev = -1;
    setInterval(() => {
      const moving = v.currentTime !== prev;
      prev = v.currentTime;
      const verdict = document.documentElement.classList.contains('no-autoplay')
        ? 'GAVE UP — showing the still instead'
        : moving ? 'PLAYING' : (document.hidden ? 'tab in background' : 'NOT MOVING');
      box.textContent =
        'HERO VIDEO: ' + verdict +
        '\ntime ' + v.currentTime.toFixed(2) + ' / ' + (v.duration || 0).toFixed(2) +
        '   paused ' + v.paused +
        '\nloaded ' + v.readyState + '/4   on screen ' + onScreen +
        '   tab hidden ' + document.hidden +
        '\nreduced-motion ' + REDUCED.matches + '   failed tries ' + strikes +
        '\nfile ' + (v.currentSrc.split('/').pop() || 'none');
    }, 500);
  }

  /* ------------------------------------------------------------------ *
   * 3d. pointer depth + specular
   * The docblock at the top of this file has claimed this feature since the
   * first version and never implemented it. It is the single thing that
   * turns the product tiles from flat pictures into objects: the card takes
   * a small parallax tilt and a highlight tracks the cursor, the way a real
   * stone's specular tracks the observer.
   * ------------------------------------------------------------------ */
  function initPointerDepth() {
    if (REDUCED.matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const MAX = 6;   // degrees. Above ~8 it reads as a gimmick, not depth.
    document.querySelectorAll('.card, .shelf__item').forEach((card) => {
      const frame = card.querySelector('.card__frame, .shelf__frame');
      if (!frame) return;
      let rect = null;

      card.addEventListener('pointerenter', () => { rect = card.getBoundingClientRect(); });
      card.addEventListener('pointermove', (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        frame.style.setProperty('--rx', (-y * MAX).toFixed(2) + 'deg');
        frame.style.setProperty('--ry', (x * MAX).toFixed(2) + 'deg');
        frame.style.setProperty('--mx', ((x + 0.5) * 100).toFixed(1) + '%');
        frame.style.setProperty('--my', ((y + 0.5) * 100).toFixed(1) + '%');
      });
      card.addEventListener('pointerleave', () => {
        frame.style.setProperty('--rx', '0deg');
        frame.style.setProperty('--ry', '0deg');
        rect = null;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. counters
   * ------------------------------------------------------------------ */
  function initCounters() {
    const els = document.querySelectorAll('[data-count]');
    if (!els.length) return;

    const run = (el) => {
      const target = parseFloat(el.dataset.count);
      const dur = parseInt(el.dataset.countDur || 1400, 10);
      const suffix = el.dataset.countSuffix || '';
      if (REDUCED.matches) {
        el.textContent = target + suffix;
        return;
      }
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / dur, 1);
        // easeOutExpo — fast commit, soft landing
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        el.textContent = Math.round(target * eased) + suffix;
        if (t < 1) raf(tick);
      };
      raf(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          run(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.5 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------ *
   * 5. scroll progress + header state
   * ------------------------------------------------------------------ */
  function initScrollState() {
    const header = document.querySelector('[data-header]');
    const bar = document.querySelector('[data-scroll-progress]');
    if (!header && !bar) return;

    let last = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;

      if (bar) bar.style.setProperty('--progress', max > 0 ? y / max : 0);

      if (header) {
        header.classList.toggle('is-stuck', y > 24);
        // hide on scroll down, reveal on scroll up — but never near the top
        if (y > 320 && y > last + 4) header.classList.add('is-hidden');
        else if (y < last - 4) header.classList.remove('is-hidden');
      }
      last = y;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        raf(update);
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------------------------------ *
   * 6. marquee — duplicate content until it overflows twice, then translate
   * ------------------------------------------------------------------ */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach((el) => {
      const track = el.querySelector('.marquee__track');
      if (!track || track.dataset.marqueeDone) return;
      const original = track.innerHTML;
      // ensure the track is at least 2x the container so the loop is seamless
      let guard = 0;
      while (track.scrollWidth < el.offsetWidth * 2 && guard++ < 12) {
        track.innerHTML += original;
      }
      track.innerHTML += track.innerHTML; // the duplicate half that loops
      track.dataset.marqueeDone = '1';
      const speed = parseFloat(el.dataset.marquee) || 60; // px per second
      track.style.setProperty('--marquee-dur', `${track.scrollWidth / 2 / speed}s`);
    });
  }

  /* ------------------------------------------------------------------ *
   * 7. accordion + mobile nav — small stateful UI
   * ------------------------------------------------------------------ */
  function initDisclosure() {
    document.querySelectorAll('.acc__btn').forEach((btn) => {
      const panel = btn.nextElementSibling;
      if (!panel) return;
      const inner = panel.firstElementChild;

      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (open) {
          // from its current px height down to 0, so the transition has
          // two definite endpoints to interpolate between
          panel.style.height = panel.scrollHeight + 'px';
          raf(() => { panel.style.height = '0px'; });
        } else {
          panel.style.height = (inner ? inner.offsetHeight : panel.scrollHeight) + 'px';
        }
      });

      // once open, drop the fixed height so the panel can reflow with the
      // text if the viewport changes
      panel.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'height') return;
        if (btn.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
      });
    });

    const burger = document.querySelector('[data-burger]');
    const nav = document.querySelector('[data-nav]');
    const header = document.querySelector('[data-header]');
    if (burger && nav) {
      // Closed, the mobile nav is hidden by clip-path alone — which leaves
      // its 7 links in the tab order while invisible. `inert` removes them
      // (and from the a11y tree); cleared the moment the sheet opens. Desktop
      // must never be inert: the media check mirrors the CSS breakpoint.
      const mobile = window.matchMedia('(max-width: 860px)');
      const syncInert = () => {
        const open = burger.getAttribute('aria-expanded') === 'true';
        if (mobile.matches && !open) nav.setAttribute('inert', '');
        else nav.removeAttribute('inert');
      };
      const setOpen = (open) => {
        burger.setAttribute('aria-expanded', String(open));
        nav.classList.toggle('is-open', open);
        // .nav-open on the header doubles as the :has() fallback (Firefox
        // <121 has no :has()) so the open sheet never inherits the header's
        // backdrop blur.
        if (header) header.classList.toggle('nav-open', open);
        syncInert();
      };
      burger.addEventListener('click', () => {
        setOpen(burger.getAttribute('aria-expanded') !== 'true');
      });
      // Escape closes the sheet and hands focus back to the button
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && burger.getAttribute('aria-expanded') === 'true') {
          setOpen(false);
          burger.focus();
        }
      });
      // a resize past the breakpoint must not leave the panel half-open
      window.addEventListener('resize', () => {
        if (window.innerWidth > 860) setOpen(false);
        else syncInert();
      });
      syncInert();
    }

    // option chips behave as a single-select group — and the choice TRAVELS.
    // It used to toggle a highlight and go nowhere: a customer could pick
    // "22 inch" and both CTAs would send the enquiry without it.
    const carryChoice = () => {
      const parts = [];
      document.querySelectorAll('[data-chips]').forEach((g) => {
        const on = g.querySelector('.chip[aria-pressed="true"]');
        if (on) parts.push(g.dataset.optLabel + ': ' + on.textContent.trim());
      });
      if (!parts.length) return;
      const q = encodeURIComponent(parts.join(' · '));
      document.querySelectorAll('[data-piece-cta]').forEach((a) => {
        const base = a.dataset.baseHref || a.getAttribute('href');
        a.dataset.baseHref = base;
        a.href = base + (base.indexOf('?') > -1 ? '&' : '?') + 'opt=' + q;
      });
      document.querySelectorAll('a[href^="mailto:"]').forEach((a) => {
        if (a.href.indexOf('subject=') === -1) return;
        const base = a.dataset.baseHref || a.getAttribute('href');
        a.dataset.baseHref = base;
        a.href = base + '&body=' + q;
      });
    };
    document.querySelectorAll('[data-chips]').forEach((group) => {
      group.addEventListener('click', (e) => {
        const chip = e.target.closest('.chip');
        if (!chip || !group.contains(chip)) return;
        group.querySelectorAll('.chip').forEach((c) =>
          c.setAttribute('aria-pressed', String(c === chip))
        );
        carryChoice();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */
  /* The homepage glides on Lenis momentum; the other 13 pages ran bare
     native scroll — the seam between the two shells you could FEEL. Same
     tuning as js/bj-elevate.js, driven by rAF since these pages carry no
     GSAP. Wheel only: touch keeps the native fling, reduced motion keeps
     everything native. */
  function initLenis() {
    if (REDUCED.matches || !window.Lenis) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const lenis = new window.Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    // base.css sets html{scroll-behavior:smooth}, which double-animates
    // every anchor jump against Lenis
    document.documentElement.style.scrollBehavior = 'auto';
  }

  function boot() {
    document.documentElement.classList.add('js');
    initLenis();
    splitText();
    initReveal();
    initScrub();
    initHeroVideo();
    initPointerDepth();
    initParallax();
    initCounters();
    initScrollState();
    initMarquee();
    initDisclosure();
    // page-load choreography starts only once fonts are settled, so type
    // never reveals in a fallback face and then swaps mid-animation.
    const start = () => document.documentElement.classList.add('is-ready');
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(start);
      setTimeout(start, 1200); // hard failsafe
    } else {
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
